import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import { formatDistanceToNow } from "date-fns";
import { ApiRouteComment, ApiRouteCommentOutcome } from "griptonite-api-client";

import { useArchiveRouteCommentMutation, useRouteInboxCommentsQuery } from "@/hooks/routeComments";

import { FormSection } from "../RouteForm/RouteFormFields";

// The buttons sit on the alert's tint. A white fill keeps them looking like buttons there,
// where a transparent outline looks washed out and a soft neutral fill looks like a grey patch.
const buttonSx = {
  backgroundColor: "background.surface",
  "&:hover": { backgroundColor: "background.level1" },
} as const;

const pluralise = (count: number, noun: string) => (count === 1 ? noun : `${noun}s`);

const TimeAgo = ({ timestamp }: { timestamp: string }) => {
  const date = new Date(timestamp);
  return (
    <Tooltip title={date.toLocaleString()} placement="top">
      <span>{formatDistanceToNow(date, { addSuffix: true })}</span>
    </Tooltip>
  );
};

const RouteComment = ({ comment }: { comment: ApiRouteComment }) => {
  const archiveMutation = useArchiveRouteCommentMutation();
  const isUpdating = archiveMutation.isPending;

  const archive = (outcome: ApiRouteCommentOutcome) =>
    archiveMutation.mutate({ commentUuid: comment.uuid, outcome });

  return (
    <Stack spacing={0.5}>
      <Typography level="body-xs">
        <Typography fontWeight="lg" textColor="text.secondary">
          {comment.user.name}
        </Typography>
        {" · "}
        <TimeAgo timestamp={comment.timestamp} />
      </Typography>
      <Typography level="body-sm" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {comment.text}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ paddingTop: 0.5 }}>
        {/* This sits inside the route form, so the buttons must not submit it. */}
        <Button
          type="button"
          size="sm"
          variant="outlined"
          color="neutral"
          sx={buttonSx}
          disabled={isUpdating}
          onClick={() => archive("IGNORED")}
        >
          Ignore
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outlined"
          color="success"
          sx={buttonSx}
          disabled={isUpdating}
          onClick={() => archive("HANDLED")}
        >
          Mark handled
        </Button>
      </Stack>
      {archiveMutation.isError ? (
        <Typography level="body-xs" color="danger">
          {"Couldn't update the comment. Please try again."}
        </Typography>
      ) : null}
    </Stack>
  );
};

/**
 * The climber comments waiting in a route's staff inbox, which can each be
 * ignored or marked handled. Once archived a comment leaves the list, so the
 * archive isn't shown here. The list sits in an alert, collapsed until opened,
 * with the count in its heading. Renders nothing when the inbox is empty. Key
 * it by route so each route starts collapsed.
 */
export default function RouteComments({ routeUuid }: { routeUuid: string }) {
  const { data: comments } = useRouteInboxCommentsQuery(routeUuid);

  if (!comments?.length) {
    return null;
  }

  return (
    <FormSection>
      <Alert color="warning" variant="soft" sx={{ display: "block", paddingY: 0.5 }}>
        <Accordion>
          {/* Matching the alert's colour keeps the hover tint from going grey. */}
          <AccordionSummary
            color="warning"
            slotProps={{ button: { sx: { paddingX: 0, paddingY: 0.5 } } }}
          >
            <Typography level="title-sm">
              {comments.length} {pluralise(comments.length, "comment")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails slotProps={{ content: { sx: { paddingX: 0 } } }}>
            <Stack
              spacing={1.5}
              divider={<Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />}
            >
              {comments.map((comment) => (
                <RouteComment key={comment.uuid} comment={comment} />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Alert>
    </FormSection>
  );
}
